import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

export type SystemAdminRole = 'system_admin' | 'system_operator' | 'system_support';

export interface SystemAdminFormData {
  email: string;
  name: string;
  role: SystemAdminRole;
  vendorId: string;
}

interface Vendor {
  id: string;
  name: string;
}

interface SystemAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SystemAdminFormData) => Promise<void>;
  initialData?: {
    name: string;
    email: string;
    role: SystemAdminRole;
    vendorId?: string;
  } | null;
  vendors: Vendor[];
  onDelete?: () => void;
  deleteConfirmationName?: string;
  onDeleteConfirmationNameChange?: (name: string) => void;
}

const ROLE_COLORS: Record<SystemAdminRole, string> = {
  system_admin: 'bg-red-100 text-red-800',
  system_operator: 'bg-blue-100 text-blue-800',
  system_support: 'bg-green-100 text-green-800',
};

export function SystemAdminModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  vendors,
  onDelete,
  deleteConfirmationName = '',
  onDeleteConfirmationNameChange,
}: Readonly<SystemAdminModalProps>) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<SystemAdminFormData>({
    email: '',
    name: '',
    role: 'system_operator',
    vendorId: '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        email: initialData.email,
        name: initialData.name,
        role: initialData.role,
        vendorId: initialData.vendorId || '',
      });
    } else {
      setFormData({
        email: '',
        name: '',
        role: 'system_operator',
        vendorId: '',
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {initialData ? t('staff.modal.editTitle') : t('staff.modal.createTitle')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="admin-name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('staff.fields.name')} <span className="text-red-500">*</span>
            </label>
            <input
              id="admin-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder={t('staff.fields.namePlaceholder')}
              required
            />
          </div>
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('staff.fields.email')} <span className="text-red-500">*</span>
            </label>
            <input
              id="admin-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder={t('staff.fields.emailPlaceholder')}
              required
              disabled={!!initialData}
            />
          </div>
          <div>
            <label htmlFor="admin-role" className="block text-sm font-medium text-gray-700 mb-1">
              {t('staff.fields.role')} <span className="text-red-500">*</span>
            </label>
            <select
              id="admin-role"
              value={formData.role}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  role: e.target.value as SystemAdminRole,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {(Object.keys(ROLE_COLORS) as SystemAdminRole[]).map((role) => (
                <option key={role} value={role}>
                  {t(`staff.roles.${role}.label`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="admin-vendor" className="block text-sm font-medium text-gray-700 mb-1">
              {t('staff.fields.vendor')} <span className="text-red-500">*</span>
            </label>
            <select
              id="admin-vendor"
              value={formData.vendorId}
              onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            >
              <option value="">{t('auth.selectVendor')}</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{initialData ? t('common.save') : t('common.create')}</Button>
          </div>
        </form>

        {/* Danger Zone */}
        {initialData && onDelete && (
          <div className="mt-8 pt-6 border-t border-red-100 bg-red-50 -mx-6 px-6 pb-2">
            <h3 className="text-red-800 font-bold mb-2 flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> {t('common.confirm.dangerZone')}
            </h3>
            <p className="text-xs text-red-600 mb-4 whitespace-pre-line">{t('staff.modal.deleteNote')}</p>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={initialData.name}
                value={deleteConfirmationName}
                onChange={(e) => onDeleteConfirmationNameChange?.(e.target.value)}
                className="flex-1 text-sm border border-red-200 rounded px-2"
              />
              <Button variant="destructive" disabled={deleteConfirmationName !== initialData.name} onClick={onDelete}>
                {t('staff.modal.executeDelete')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
